import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useApi, ApiError } from "../api/client";
import { ProfileTileModal } from "../components/ProfileTiles";
import type { CohortFilters } from "./reportTypes";

/**
 * Drill-through to source (ADR-011). Opens the whānau behind one reporting
 * cell — a subdomain × movement category over the current cohort — from
 * GET /api/v1/reports/drill. Super-admin only (server-enforced + audited);
 * each whānau links to their profile, which runs its own access checks.
 */
export type DrillCategory = "regressed" | "unchanged" | "improved";

type DrillRow = { whanau_id: number; display_name: string };
type DrillResponse = { rows: DrillRow[]; count: number };

type Props = {
  subdomainId: string;
  subdomainLabel: string;
  category: DrillCategory;
  filters: CohortFilters;
  onClose: () => void;
};

function toQuery(f: CohortFilters): URLSearchParams {
  const p = new URLSearchParams();
  for (const v of f.location) p.append("location", v);
  for (const v of f.iwi) p.append("iwi", v);
  for (const v of f.service) p.append("service", v);
  for (const v of f.durationBand) p.append("duration_band", v);
  return p;
}

type State =
  | { kind: "loading" }
  | { kind: "ok"; rows: DrillRow[] }
  | { kind: "error"; message: string };

export function DrillModal({
  subdomainId,
  subdomainLabel,
  category,
  filters,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const callApi = useApi();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setState({ kind: "loading" });
      const params = toQuery(filters);
      params.set("subdomain", subdomainId);
      params.set("category", category);
      try {
        const data = await callApi<DrillResponse>(
          `/reports/drill?${params.toString()}`,
        );
        if (!cancelled) setState({ kind: "ok", rows: data.rows });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setState({ kind: "error", message });
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
    // callApi is a fresh closure each render; deliberately excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdomainId, category, filters]);

  const title = t("reports.drill.title", {
    subdomain: subdomainLabel,
    category: t(`reports.drill.category.${category}`),
  });

  return (
    <ProfileTileModal title={title} onClose={onClose}>
      {state.kind === "loading" && (
        <p className="rpt__status">{t("reports.loading")}</p>
      )}
      {state.kind === "error" && (
        <p className="rpt__status rpt__status--error">
          {t("reports.error", { message: state.message })}
        </p>
      )}
      {state.kind === "ok" &&
        (state.rows.length === 0 ? (
          <p className="rpt__status">{t("reports.drill.empty")}</p>
        ) : (
          <>
            <p className="rpt__count">
              {t("reports.drill.count", { n: state.rows.length })}
            </p>
            <ul
              style={{
                listStyle: "none",
                margin: "8px 0 0",
                padding: 0,
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              {state.rows.map((r) => (
                <li key={r.whanau_id}>
                  <Link
                    to={`/whanau/${String(r.whanau_id)}`}
                    onClick={onClose}
                    style={{
                      display: "block",
                      padding: "10px 8px",
                      borderRadius: "6px",
                      textDecoration: "none",
                    }}
                  >
                    {r.display_name}
                  </Link>
                </li>
              ))}
            </ul>
            <p
              style={{
                marginTop: "12px",
                fontSize: "0.8rem",
                color: "var(--ink-muted, #5a6472)",
              }}
            >
              {t("reports.drill.note")}
            </p>
          </>
        ))}
    </ProfileTileModal>
  );
}

export default DrillModal;
